/**
 * ResBalanceExplainer — explainer for the RES.BALANCE Dual Pair Guarantee.
 *
 * RES.BALANCE pairs RES.CONSUME (R1, linear category) with RES.RELEASE
 * (R2, compact closed) along the (−⊗A) ⊣ (A*⊗−) adjunction. Deploying
 * both sides is strictly stronger than deploying either alone: the
 * adjunction unit/counit witness that budget consumption and obligation
 * discharge are mutual sections, so a budget-only or a release-only
 * discipline leaves a structural gap.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  AdjunctionDiagram,
  CounterModelGrid,
  ExplainerCard,
  ResponsiveSplit,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import {
  COMPOSITIONALITY_PRINCIPLE_COLOR,
  DUAL_PAIR_COLOR,
  GRAY,
  THEOREM_COLOR,
  withAlpha,
} from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";

const COUNTER_MODELS = [
  {
    title: "R1 alone — consume without release",
    body: "Every operation has a typed cost and total spend is bounded — but acquired handles are never required to be discharged. The budget balances while file descriptors and locks leak: the spend story is real, the lifecycle story is missing.",
  },
  {
    title: "R2 alone — release without consume",
    body: "Every acquisition is matched by a discharge — but nothing bounds how much may be acquired in the first place. Resources are always released, yet an unbounded acquisition burst exhausts the host: the lifecycle story is real, the budget story is vacuous.",
  },
] as const;

const ACCENT = DUAL_PAIR_COLOR["RES.BALANCE"].color;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-res-balance",
  kind: "SSM · Dual Pair Guarantee (S1→S1)",
  title: "RES.BALANCE",
  subtitle: "R1 + R2 · (−⊗A) ⊣ (A*⊗−)",
  accent: ACCENT,
  accentAlt: COMPOSITIONALITY_PRINCIPLE_COLOR.ACCOUNT.color,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  {
    label: "RES.CONSUME (R1)",
    href: "#explainer-res-consume",
    accent: THEOREM_COLOR["RES.CONSUME"],
  },
  {
    label: "RES.RELEASE (R2)",
    href: "#explainer-res-release",
    accent: THEOREM_COLOR["RES.RELEASE"],
  },
  {
    label: "ACCOUNT (SDP dual)",
    href: "#explainer-account",
    accent: COMPOSITIONALITY_PRINCIPLE_COLOR.ACCOUNT.color,
  },
  { label: "Compact-closed", href: "#explainer-compact-closed" },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>RES.BALANCE</strong> says: every resource is both spent within budget <em>and</em>
      discharged when its scope ends — and those two facts cohere. One side alone leaves a gap.
    </p>
  ),
  intuition: (
    <>
      <p>
        RES.CONSUME alone bounds total spend: no operation exceeds its typed cost. RES.RELEASE alone
        guarantees discharge: anything acquired in a scope is released when the scope exits. But a
        bounded budget that leaks handles, or a leak-free lifecycle with no spending cap, each
        leaves the resource lifecycle half-told.
      </p>
      <p>
        RES.BALANCE is the joint guarantee, and it is <strong>strictly stronger</strong> than either
        side alone. The adjunction{" "}
        <MathInline>{tex`(- \otimes A) \dashv (A^{*} \otimes -)`}</MathInline> certifies that
        consuming a unit of <MathInline>{tex`A`}</MathInline> and discharging its dual{" "}
        <MathInline>{tex`A^{*}`}</MathInline> are mutual sections: the resource lifecycle closes, by
        construction.
      </p>
    </>
  ),
  formal: (
    <>
      <p>In the resource-monoidal category, RES.BALANCE pairs:</p>
      <ul>
        <li>
          <strong>R1.</strong> Linear consumption — the functor{" "}
          <MathInline>{tex`(- \otimes A)`}</MathInline> tensoring in a budget unit, the left
          adjoint.
        </li>
        <li>
          <strong>R2.</strong> Compact-closed release — the functor{" "}
          <MathInline>{tex`(A^{*} \otimes -)`}</MathInline> for the dual obligation, the right
          adjoint.
        </li>
      </ul>
      <p>
        The adjunction <MathInline>{tex`(- \otimes A) \dashv (A^{*} \otimes -)`}</MathInline> has
        unit (coevaluation, acquire) and counit (evaluation, release).{" "}
        <strong>Deploying both is strictly stronger than deploying either alone</strong>: the snake
        equations force every consumed unit to be matched by a discharge, closing the gap each
        component leaves. RES.BALANCE is a projection of the Categorical Immunity Theorem, not an
        independent story.
      </p>
      <AdjunctionDiagram
        leftCat="Consume"
        rightCat="Release"
        leftAdjoint="(−⊗A)"
        rightAdjoint="(A*⊗−)"
        accent={ACCENT}
        ariaLabel="RES.BALANCE adjunction: (−⊗A) left-adjoint to (A*⊗−) with coevaluation unit and evaluation counit"
      />
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Why "strictly stronger".</strong> Each component admits a counter-model the other
        rules out:
      </p>
      <CounterModelGrid counterModels={COUNTER_MODELS} ariaLabel="RES.BALANCE counter-models" />
      <p style={{ marginTop: "0.75rem" }}>
        Combining R1 and R2 along the adjunction closes both gaps in one envelope. The joint
        guarantee neutralizes the full resource-abuse lifecycle: a path that is budget-clean but
        leak-dirty (or vice versa) is <em>structurally inexpressible</em>. Both components run in{" "}
        <strong>CONTAINED</strong> mode — execution proceeds, but damage cannot exceed the
        structural envelope.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> Bypassing RES.BALANCE requires bypassing both
        RES.CONSUME and RES.RELEASE, or escaping into an unmetered host primitive — both moves leave
        the kernel category and so vacate the guarantee rather than violate it. With its SDP dual{" "}
        <code>ACCOUNT</code> it closes as the <code>ICG-St</code> Safety Envelope.
      </p>
    </>
  ),
  references: [
    {
      label: "Theorem Canon — Dual Pair Guarantees (§6)",
      path: "docs/reference/Theorem-Canon.md",
      note: "Section 6: Dual Pair Guarantees; the strict-strengthening claim.",
    },
    {
      label: "RES.CONSUME kernel module",
      path: "agda/Kernel/RES/CONSUME/Neutralization.agda",
    },
    {
      label: "RES.RELEASE kernel module",
      path: "agda/Kernel/RES/RELEASE/Neutralization.agda",
    },
  ],
};

const DIAGRAM = (
  <ResponsiveSplit
    ariaLabel="RES.BALANCE component theorems side by side"
    left={
      <div
        style={{
          padding: "0.5rem",
          backgroundColor: withAlpha(THEOREM_COLOR["RES.CONSUME"], "1A"),
          borderRadius: "0.375rem",
        }}
      >
        <div
          style={{
            fontSize: "0.6875rem",
            fontWeight: 700,
            color: THEOREM_COLOR["RES.CONSUME"],
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            marginBottom: "0.25rem",
          }}
        >
          R1 — Consume (left adjoint)
        </div>
        <code style={{ fontSize: "0.8125rem", color: GRAY[700] }}>(−⊗A) : spend budget</code>
      </div>
    }
    right={
      <div
        style={{
          padding: "0.5rem",
          backgroundColor: withAlpha(THEOREM_COLOR["RES.RELEASE"], "1A"),
          borderRadius: "0.375rem",
        }}
      >
        <div
          style={{
            fontSize: "0.6875rem",
            fontWeight: 700,
            color: THEOREM_COLOR["RES.RELEASE"],
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            marginBottom: "0.25rem",
          }}
        >
          R2 — Release (right adjoint)
        </div>
        <code style={{ fontSize: "0.8125rem", color: GRAY[700] }}>(A*⊗−) : discharge</code>
      </div>
    }
  />
);

export function ResBalanceExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={DIAGRAM} crossLinks={CROSS_LINKS} />
  );
}
